import React, { useState, useEffect } from 'react';
import apiService from '../utils/api';
import Loader from './Loader';
import Sidebar from './sidebar/Sidebar';
import './ContractStatus.css';
import Snackbar from '@mui/material/Snackbar';
import Delete from '@mui/icons-material/Delete';
import Breadcrumbs from './Breadcrumbs'; 

const ContractStatus = () => {
  const [contractStatuses, setContractStatuses] = useState([]);
  const [filteredContractStatuses, setFilteredContractStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newContractStatusName, setNewContractStatusName] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContractStatusName, setEditContractStatusName] = useState('');
  const [editContractStatusDescription, setEditContractStatusDescription] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); 
  const [deleteContractStatusId, setDeleteContractStatusId] = useState(null); 
  const [newContractStatusDescription, setNewContractStatusDescription] = useState('');

  const fetchContractStatuses = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.fetchData('metadata/contract-status');
      const contractStatusesWithDescriptions = response.map(status => ({
        ...status,
        description: status.description || 'No description available'
      }));
      setContractStatuses(contractStatusesWithDescriptions || []);
      setFilteredContractStatuses(contractStatusesWithDescriptions || []);
    } catch (err) {
      console.error('Error fetching contract statuses:', err);
      setError('Failed to load contract statuses. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContractStatuses(); // Call the function
  }, []);

  useEffect(() => {
    const filtered = contractStatuses.filter(status =>
      status.value.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredContractStatuses(filtered);
  }, [searchQuery, contractStatuses]);

const handleAddContractStatus = async () => {
    if (!newContractStatusName.trim()) {
      setSnackbarMessage('Contract status name is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const newContractStatus = {
        status_name: newContractStatusName,
        description: newContractStatusDescription || 'No Description',
        updated_by: sessionStorage.getItem('user_id') || ''
      };
      const response = await apiService.postData('create-contract-status', newContractStatus);
      if (response.status === 'success') {
        await fetchContractStatuses(); // Refetch the list
        setNewContractStatusName('');
        setNewContractStatusDescription('');
        setIsModalOpen(false);
        setSnackbarMessage('Contract status added successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to add contract status');
      }
    } catch (err) {
      console.error('Error adding contract status:', err);
  const errorMessage = err.response?.status === 500 
    ? 'Contract status already exists' 
    : (err.response?.data?.message || err.message || 'Failed to add contract status. Please try again later.');
  setSnackbarMessage(errorMessage);
  setIsSuccess(false);
  setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdit = async (id) => {
    if (!editContractStatusName.trim()) {
      setSnackbarMessage('Contract status name is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }
    try {
      setIsLoading(true);
      const originalStatus = contractStatuses.find(status => status.id === id);
      const updatedValues = {};
      if (originalStatus.value !== editContractStatusName) {
        updatedValues.StatusName = editContractStatusName;
      }
      if (originalStatus.description !== editContractStatusDescription) {
        updatedValues.description = editContractStatusDescription;
      }
      const payload = {
        values: updatedValues,
        updated_by: sessionStorage.getItem('user_id')
      };
      if (Object.keys(updatedValues).length === 0) {
        setSnackbarMessage('No changes detected');
        setIsSuccess(false);
        setSnackbarOpen(true);
        return;
      }
      const response = await apiService.putData(`update-contract-status/${id}`, payload); // Use id directly
      if (response.status === 'success') {
        await fetchContractStatuses(); // Refetch the list
        setSnackbarMessage('Contract status updated successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to update contract status');
      }
    } catch (err) {
      console.error('Error updating contract status:', err);
      setSnackbarMessage(err.message || 'Failed to update contract status. Please try again later.');
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setIsEditModalOpen(false);
      setEditingId(null);
      setEditContractStatusName('');
      setEditContractStatusDescription('');
    }
  };

  const handleDeleteContractStatus = async (id) => {
    try {
      setIsLoading(true);
      const response = await apiService.deleteData(`metadata/contract-status/${id}`);
      if (response.status === 'success') {
        await fetchContractStatuses(); // Refetch the list
        setSnackbarMessage('Contract status deleted successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to delete contract status');
      }
    } catch (err) {
      console.error('Error deleting contract status:', err);
      setSnackbarMessage(err.message || 'Failed to delete contract status. Please try again later.');
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteContractStatusId(null);
    }
  };

  return (
    <div className="departments-page-container">
      <Sidebar />
      <div className="departments-page-content">
         <Breadcrumbs/>
        <div className="departments-header">
          <h2 style={{ color: '#ff6b35', fontSize: '24px' }}>Contract Status</h2>
          <button
            className="add-department-button"
            onClick={() => setIsModalOpen(true)}
          >
            Add Contract Status
          </button>
        </div>
        {isLoading ? (
          <Loader isLoading={isLoading} message="Loading Contract Statuses..." color="#ff6b35" />
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search contract statuses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <svg
                className="department-search-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <table className="departments-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Contract Status</th>
                  <th>Contract Status Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContractStatuses.length > 0 ? (
                  filteredContractStatuses.map((status, index) => (
                    <tr key={status.id}>
                      <td>{index + 1}</td>
                      <td>{status.value}</td>
                      <td>{status.description || 'No description available'}</td>
                     <td>
  <svg
    className="edit-icon"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ff6b35"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ cursor: 'pointer', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }}
    onClick={() => {
      setIsEditModalOpen(true);
      setEditingId(status.id);
      setEditContractStatusName(status.value);
      setEditContractStatusDescription(status.description || '');
    }}
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
  <Delete
    style={{
      cursor: 'pointer',
      width: '20px',
      height: '20px',
      color: '#ff6b35',
      display: 'inline-block',
      verticalAlign: 'middle',
    }}
    titleAccess="Delete Contract Status"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log(`Delete icon clicked for contract status ID: ${status.id}`); // Debug click
      setDeleteContractStatusId(status.id);
      setIsDeleteModalOpen(true);
    }}
  />
</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No contract statuses found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="department-modal-content">
              <div className="contract_types-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Add Contract Status</h3>
              </div>
              <div className="department-modal-form">
  <label htmlFor="contract-status-name" className='department-label'>Contract Status Name</label>
  <input
    id="contract-status-name"
    type="text"
    value={newContractStatusName}
    onChange={(e) => setNewContractStatusName(e.target.value)}
    placeholder="Enter contract status name"
    className="modal-input"
  />
  <label htmlFor="contract-status-description" className='department-label'>Description</label>
  <input
    id="contract-status-description"
    type="text"
    value={newContractStatusDescription}
    onChange={(e) => setNewContractStatusDescription(e.target.value)}
    placeholder="Enter description"
    className="modal-input"
  />
</div>
              <div className="department-modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={handleAddContractStatus}
                >
                  Save
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
  setNewContractStatusName('');
  setNewContractStatusDescription('');
  setIsModalOpen(false);
}}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {isEditModalOpen && (
          <div className="modal-overlay">
            <div className="department-modal-content">
              <div className="contract_types-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Update Contract Status</h3>
              </div>
              <div className="department-modal-form">
                <label htmlFor="edit-contract-status-name" className='department-label'>Contract Status Name</label>
                <input
                  id="edit-contract-status-name"
                  type="text"
                  value={editContractStatusName}
                  onChange={(e) => setEditContractStatusName(e.target.value)}
                  placeholder="Enter contract status name"
                  className="modal-input"
                />
                <label htmlFor="edit-contract-status-description" className='department-label'>Description</label>
                <input
                  id="edit-contract-status-description"
                  type="text"
                  value={editContractStatusDescription}
                  onChange={(e) => setEditContractStatusDescription(e.target.value)}
                  placeholder="Enter description"
                  className="modal-input"
                />
              </div>
              <div className="department-modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={() => handleSaveEdit(editingId)}
                >
                  Save
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingId(null);
                    setEditContractStatusName('');
                    setEditContractStatusDescription('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {isDeleteModalOpen && (
  <div className="modal-overlay">
    <div className="department-modal-content">
      <div className="contract_types-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
        <h3 style={{ margin: 0 }}>Confirm Delete</h3>
      </div>
      <div className="department-modal-form">
        <p>Do you want to delete this contract status?</p>
      </div>
      <div className="department-modal-buttons">
        <button
          className="modal-button save-button"
          onClick={() => handleDeleteContractStatus(deleteContractStatusId)}
        >
          Delete
        </button>
        <button
          className="modal-button cancel-button"
          onClick={() => {
            setIsDeleteModalOpen(false);
            setDeleteContractStatusId(null);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
        <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                sx={{
                  '& .MuiSnackbarContent-root': {
                    backgroundColor: isSuccess ? '#4caf50' : '#f44336',
                  },
                  zIndex: 1400,
                }}
              />
      </div>
    </div>
  );
};

export default ContractStatus;